
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { PERMISSION } from '@/permissions';
import TextButton from '@/components/shared/suites/TextButton.vue';
import DeliveryModal from '@/components/advanced/DeliveryModal.vue';
import GenericTableLocationsComponent from '@/components/advanced/GenericTableLocations.vue';

@Component<FulfilmentSettings>({
	components: {
		ValidationObserver,
		TextButton,
		DeliveryModal,
		GenericTableLocationsComponent
	}
})
export default class FulfilmentSettings extends Vue {
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Watch('restaurant')
	private onRestaurantChange(): void {
		this.setFulfilmentData();
	}

	$refs!: { observer: Validate };

	private PERMISSION = PERMISSION;

	// Delivery
	private deliveryEnabled: boolean = false;
	private deliveryChargeTypeIsDollar: number = 2;
	private deliveryInfo: DeliveryInfo|any|null = null;
	private showDeliveryModal: boolean = false;

	// Generic Table Locations
	private genericTableLocationsEnabled: boolean = false;
	private genericTableLocations: GenericTableLocations|null = null;

	private created(): void {
		this.setFulfilmentData();
	}

	private setFulfilmentData(): void {
		this.setDeliveryInfo();
		this.setGenericTableLocations();
	}

	/**
	 * Set delivery info depending on if null or not
	 *
	 * @return {void}
	 */
	private setDeliveryInfo(): void {
		if (this.restaurant.delivery && this.restaurant.delivery.type) {
			this.deliveryEnabled = true;
			this.deliveryInfo = this.restaurant.delivery;
			if (this.restaurant.delivery.delivery_charge) {
				this.deliveryChargeTypeIsDollar = this.restaurant.delivery.delivery_charge!.slice(this.restaurant.delivery.delivery_charge!.length - 1) === '$' ? 0 : 1;
				this.deliveryInfo.delivery_charge = this.restaurant.delivery.delivery_charge;
			}
			else if (this.restaurant.delivery.delivery_fee_ranges?.length) {
				const firstFee = this.restaurant.delivery.delivery_fee_ranges[0].fee;
				if (firstFee) {
					this.deliveryChargeTypeIsDollar = firstFee.endsWith('$') ? 0 : 1;
				}
			}
		}
		else {
			this.deliveryEnabled = false;
			this.deliveryInfo = {};
		}
	}

	/**
	 * Set generic table locations info
	 *
	 * @return {void}
	 */
	private setGenericTableLocations(): void {
		this.genericTableLocations = this.restaurant.generic_table_locations;
		this.genericTableLocationsEnabled = !!(this.genericTableLocations && this.genericTableLocations.type);
	}

	/**
	 * Update generic table locations from event
	 *
	 * @param {any} genericTableLocationsObject
	 * @return {void}
	 */
	private updateGenericTableLocations(genericTableLocationsObject: any): void {
		this.genericTableLocationsEnabled = genericTableLocationsObject.enabled;
		this.genericTableLocations = genericTableLocationsObject.locations;
	}

	/**
	 * Save delivery info
	 *
	 * @param {DeliveryInfoEventObject} deliveryInfoEventObject
	 * @return {Promise<void>}
	 */
	private async updateDeliveryInfo(deliveryInfoEventObject: DeliveryInfoEventObject): Promise<void> {
		this.deliveryEnabled = deliveryInfoEventObject.enabled;
		this.deliveryChargeTypeIsDollar = deliveryInfoEventObject.type;
		this.deliveryInfo = deliveryInfoEventObject.info;
		this.showDeliveryModal = false;
	}

	/**
	 * Validate fulfilment settings and emit save event to parent components
	 * Is called by Settings.vue when the save button is clicked
	 *
	 * @return {Promise<void>}
	 */
	public async save(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (isValid) {
			const updatedRestaurant = {
				...this.restaurant,
				delivery: this.deliveryInfo,
				generic_table_locations: this.genericTableLocations
			};
			this.$emit('save-fulfilment', updatedRestaurant);
		}
	}
}
